import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import config from "../../twh/config";
import { getFirstErrorMessage } from "../../twh/functions";

const GoogleAdsCustomer = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // ✅ Fetch Customers (FINAL API FORMAT: data[])
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${config.reactApi_baseUrl}/react-api/integrations/google-ads-customer.php`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const data = await response.json();

        if (data?.data && Array.isArray(data.data)) {
          setCustomers(data.data); // 🔥 direct use
        } else {
          setCustomers([]);
          setError(data?.errorMsg || 'No customers found');
        }

      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // ✅ Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('issetGoogleAdsCustomerID', 'true');
      formData.append('google_ads_customer_id', selectedCustomer);

      const response = await fetch(
        `${config.reactApi_baseUrl}/react-api/integrations/google-ads-customer-update.php`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      const data = await response.json();

      if (data.status === true) {
        setSuccess(true);

        setTimeout(() => {
          window.location.href = "/advertising";
        }, 1200);
      } else {
        const errMsg =
          getFirstErrorMessage(data) ||
          data.errorMsg ||
          'Failed to save changes';

        setError(errMsg);
      }
    } catch (err) {
      setError('Submit error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Loader
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Changes saved successfully! Redirecting...
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card elevation={3} sx={{ px: 5 }}>

          {/* Logo */}
          <Box sx={{ textAlign: 'center', pt: 5, pb: 3 }}>
            <img
              src={`${config.reactApi_baseUrl}/media/images/google-ads.png`}
              alt="Google Ads"
              style={{ width: 150, height: 150 }}
            />
          </Box>

          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Customer Accounts
            </Typography>

            <FormControl fullWidth>
              <RadioGroup
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                {customers.length === 0 ? (
                  <Typography align="center" py={4}>
                    No customers available
                  </Typography>
                ) : (
                  customers.map((cust, index) => {
                    const hasError = cust.msg && cust.msg !== '';

                    return (
                      <Card
                        key={index}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          borderColor: hasError
                            ? 'error.main'
                            : selectedCustomer === cust.id
                            ? 'primary.main'
                            : 'grey.300',
                          bgcolor:
                            selectedCustomer === cust.id
                              ? 'action.selected'
                              : 'white',
                          transition: '0.2s',
                          '&:hover': {
                            borderColor: hasError ? 'error.dark' : 'primary.light',
                          },
                        }}
                      >
                        {hasError ? (
                          // ❌ ERROR CASE (same as PHP: no radio, just message)
                          <Box sx={{ display: 'flex', p: 2 }}>
                            <Box sx={{ mr: 2, color: 'error.main' }}>✖</Box>
                            <Box>
                              <Typography fontWeight="bold" color="error">
                                {cust.msg}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {cust.id} 
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          // ✅ NORMAL CASE
                          <FormControlLabel
                            value={cust.id}
                            control={<Radio />}
                            label={
                              <Box sx={{ py: 1 }}>
                                <Typography fontWeight="bold">
                                  {cust.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {cust.id}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              width: '100%',
                              m: 0,
                              px: 2,
                              alignItems: 'center',
                            }}
                          />
                        )}
                      </Card>
                    );
                  })
                )}
              </RadioGroup>
            </FormControl>

            {/* Submit */}
            <Box sx={{ textAlign: 'right', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !selectedCustomer}
                sx={{
                  bgcolor: '#e91e63',
                  '&:hover': { bgcolor: '#c2185b' },
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default GoogleAdsCustomer;